const API_BASE_PATH = '/api/skill-profiles';

function normalizeProfile(raw) {
  if (!raw) return null;

  // Strapi può restituire sia { id, attributes } sia documenti flat
  const { id, attributes, ...rest } = raw;
  const attrs = attributes || rest;

  const {
    documentId,
    fullName,
    bio,
    email,
    phone,
    website,
    instagram,
    facebook,
    tiktok,
    twitch,
    discord,
    isPublic,
    profileImage,
    socio,
    tags,
  } = attrs;

  let imageUrl = null;
  if (profileImage) {
    if (profileImage.url) {
      imageUrl = profileImage.url;
    } else if (profileImage.data && profileImage.data.attributes?.url) {
      imageUrl = profileImage.data.attributes.url;
    }
  }

  let normalizedTags = [];
  if (Array.isArray(tags)) {
    normalizedTags = tags.map((tag) => {
      const tagAttrs = tag.attributes || tag;
      return {
        id: tag.id || tagAttrs.id,
        name: tagAttrs?.name,
        category: tagAttrs?.category,
      };
    });
  } else if (tags && Array.isArray(tags.data)) {
    normalizedTags = tags.data.map(
      ({ id: tagId, attributes: tagAttributes }) => ({
        id: tagId,
        name: tagAttributes?.name,
        category: tagAttributes?.category,
      })
    );
  }

  let socioDocumentId = null;
  if (socio) {
    if (socio.documentId) {
      socioDocumentId = socio.documentId;
    } else if (socio.data && (socio.data.attributes || socio.data).documentId) {
      const socioAttrs = socio.data.attributes || socio.data;
      socioDocumentId = socioAttrs.documentId;
    }
  }

  return {
    id,
    documentId,
    fullName,
    bio,
    email,
    phone,
    website,
    instagram,
    facebook,
    tiktok,
    twitch,
    discord,
    isPublic,
    profileImageUrl: imageUrl,
    tags: normalizedTags,
    socioDocumentId,
    raw,
  };
}

export async function getAllProfiles() {
  const url = API_BASE_PATH;

  try {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to fetch profiles: ${response.status}`);
    }

    const json = await response.json();
    const data = Array.isArray(json.data) ? json.data : [];
    return data.map(normalizeProfile);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getAllProfiles error', error);
    throw error;
  }
}

export async function getProfileByDocumentId(documentId) {
  const url = `${API_BASE_PATH}/${documentId}`;

  try {
    const response = await fetch(url);

    if (response.status === 404) {
      return null;
    }

    if (!response.ok) {
      throw new Error(
        `Failed to fetch profile ${documentId}: ${response.status}`
      );
    }

    const json = await response.json();
    return normalizeProfile(json.data);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getProfileByDocumentId error', error);
    throw error;
  }
}

export async function getMyProfiles() {
  const url = '/api/account/skill-profiles?populate=*';

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch my profiles: ${response.status}`);
    }

    const json = await response.json();
    const data = Array.isArray(json.data) ? json.data : [];
    return data.map(normalizeProfile);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getMyProfiles error', error);
    throw error;
  }
}

export async function createMyProfile(socioDocumentId, payload) {
  const url = '/api/account/skill-profiles';

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        socioDocumentId,
        ...payload,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create profile: ${response.status}`);
    }

    const json = await response.json();
    return normalizeProfile(json.data);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('createMyProfile error', error);
    throw error;
  }
}

export async function updateMyProfile(documentId, payload) {
  const url = `/api/account/skill-profiles/${documentId}`;

  try {
    const response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(
        `Failed to update profile ${documentId}: ${response.status}`
      );
    }

    const json = await response.json();
    return normalizeProfile(json.data);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('updateMyProfile error', error);
    throw error;
  }
}

export async function removeProfileImage(documentId) {
  const url = `/api/account/skill-profiles/${encodeURIComponent(documentId)}/profile-image`;
  const response = await fetch(url, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Rimozione immagine fallita: ${response.status}`);
  }
  const text = await response.text();
  if (!text) return null;
  try {
    const json = JSON.parse(text);
    return json.data ? normalizeProfile(json.data) : null;
  } catch {
    return null;
  }
}

export async function getNetworkTags(category) {
  const params = new URLSearchParams();
  if (category) params.append('category', category);

  const url = `/api/network/tags${params.toString() ? `?${params.toString()}` : ''}`;

  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch tags: ${response.status}`);
    }

    const json = await response.json();
    const data = Array.isArray(json.data) ? json.data : [];
    return data.map((tag) => {
      const { id, attributes, ...rest } = tag;
      const attrs = attributes || rest;
      return {
        id,
        documentId: attrs.documentId,
        name: attrs.name,
        category: attrs.category,
      };
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('getNetworkTags error', error);
    throw error;
  }
}

export async function createNetworkTag({ name, category }) {
  const url = '/api/network/tags';

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, category }),
    });

    if (!response.ok) {
      throw new Error(`Failed to create tag: ${response.status}`);
    }

    const json = await response.json();
    const created = json.data;
    if (!created) return null;
    const { id, attributes, ...rest } = created;
    const attrs = attributes || rest;
    return {
      id,
      documentId: attrs.documentId,
      name: attrs.name,
      category: attrs.category,
    };
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('createNetworkTag error', error);
    throw error;
  }
}
